import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReportImportDialog } from "@/components/ReportImportDialog";
import { ReportVersions } from "@/components/ReportVersions";
import { fmtDate } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

export function DevisReportsSection({ d, onChanged }) {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canReports = hasPerm("visits.manage");
  const [options, setOptions] = useState([]);
  const [pick, setPick] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const linked = new Set((d.visit_reports || []).map((r) => r.id));

  useEffect(() => {
    if (!canReports) return;
    Promise.all([api.get("/visit-reports", { params: { site_id: d.site_id } }), api.get(`/sites/${d.site_id}`)]).then(([a, b]) => {
      const withReport = new Set(a.data.filter((r) => r.visit_id).map((r) => r.visit_id));
      setOptions([...a.data.filter((r) => !linked.has(r.id)).map((r) => ({ v: `r:${r.id}`, l: `${r.title}${r.visit_date ? ` — ${fmtDate(r.visit_date)}` : ""}` })),
        ...(b.data.visits || []).filter((v) => !withReport.has(v.id)).map((v) => ({ v: `v:${v.id}`, l: `Visite ${v.reference}${v.date ? ` — ${fmtDate(v.date)}` : ""}` }))]);
    }).catch(() => setOptions([]));
  }, [d.site_id, d.visit_reports]); // eslint-disable-line react-hooks/exhaustive-deps

  const link = async () => {
    try {
      let rid = pick.slice(2);
      if (pick.startsWith("v:")) rid = (await api.post("/visit-reports/from-visit", { visit_id: rid })).data.id;
      await api.post(`/visit-reports/${rid}/devis/${d.id}`);
      toast.success("Rapport associé au devis"); setPick(""); onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const unlink = async (rid) => {
    try { await api.delete(`/visit-reports/${rid}/devis/${d.id}`); toast.success("Association retirée (le rapport est conservé)"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="devis-reports-section">
      <div className="mb-2 flex items-center justify-between"><span className="text-xs font-semibold uppercase text-slate-400">Rapports de visite associés</span>
        {canReports && <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="devis-report-import"><Icons.Upload className="mr-1.5 h-4 w-4" />Importer un PDF</Button>}</div>
      {(d.visit_reports || []).length === 0 ? <p className="text-xs text-slate-500">Aucun rapport associé. Ces pièces n'affectent pas le PDF du devis.</p> : (
        <ul className="space-y-1.5">{d.visit_reports.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 p-2" data-testid={`devis-report-${r.id}`}>
            <Icons.FileText className="h-4 w-4 text-emerald-600" />
            <span className="min-w-0 flex-1 text-sm"><span className="font-medium text-slate-800">{r.title}</span>{r.visit_date ? <span className="text-xs text-slate-500"> · {fmtDate(r.visit_date)}</span> : null}
              <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${r.published_version ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{r.published_version ? `v${r.published_version} visible client` : "non publié"}</span></span>
            {r.visit && <Button size="sm" variant="ghost" onClick={() => navigate(`/check/${r.visit.id}`)} data-testid={`devis-report-open-${r.id}`}>Ouvrir la fiche</Button>}
            {canReports && <Button size="sm" variant="ghost" onClick={() => setManage(r.id)} data-testid={`devis-report-manage-${r.id}`}>Versions / publier</Button>}
            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => unlink(r.id)} data-testid={`devis-report-unlink-${r.id}`}>Retirer</Button>
          </li>))}</ul>)}
      {canReports && <div className="mt-2 flex gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-9 flex-1 rounded-md border border-slate-200 px-2 text-sm" data-testid="devis-report-pick">
          <option value="">Sélectionner un rapport existant du site…</option>{options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</select>
        <Button size="sm" disabled={!pick} onClick={link} className="bg-emerald-600 hover:bg-emerald-700" data-testid="devis-report-link">Associer</Button>
      </div>}
      <ReportImportDialog open={importOpen} onOpenChange={setImportOpen} devis={d} onDone={onChanged} />
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="devis-report-manage-dialog"><DialogHeader><DialogTitle>Versions et publication</DialogTitle></DialogHeader>
          {manage && <ReportVersions reportId={manage} onChanged={onChanged} />}</DialogContent>
      </Dialog>
    </div>
  );
}
